import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Shield, LogOut } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = React.useState(false);

  const navLinks = [
    { name: "Acasă", href: "/#acasa" },
    { name: "Despre Noi", href: "/#despre-noi" },
    { name: "Servicii", href: "/#servicii" },
    { name: "Galerie", href: "/#galerie" },
    { name: "De Ce Noi?", href: "/#de-ce-noi" },
    { name: "Testimoniale", href: "/#testimoniale" },
    { name: "Contact", href: "/#contact" }
  ];

  const handleNavClick = (href) => {
    setIsOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const id = href.replace("/#", "");
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      const id = href.replace("/#", "");
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav 
      data-testid="navigation-container"
      className="bg-black/80 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50 transition-all duration-300"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link 
              to="/" 
              data-testid="navbar-logo-link"
              className="flex flex-col"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <span className="font-serif-lux text-2xl sm:text-3xl tracking-wider text-[#D4AF37] font-semibold">
                ELYSIUM
              </span>
              <span className="text-[9px] tracking-[0.4em] text-white/70 uppercase">
                EVENTS SIBIU
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                data-testid={`navbar-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => handleNavClick(link.href)}
                className="text-sm font-medium text-white/80 hover:text-[#D4AF37] transition-colors uppercase tracking-wider"
              >
                {link.name}
              </button>
            ))}
          </div>

          {/* CTAs / Auth */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                <Link
                  to="/admin"
                  data-testid="navbar-admin-dashboard-link"
                  className="flex items-center space-x-1 text-sm text-[#D4AF37] hover:text-white transition-colors uppercase tracking-wider"
                >
                  <Shield size={16} />
                  <span>Admin</span>
                </Link>
                <button
                  data-testid="navbar-logout-button"
                  onClick={logout}
                  className="text-white/70 hover:text-red-400 transition-colors"
                  title="Deconectare"
                >
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <button
                data-testid="navbar-cta-button"
                onClick={() => handleNavClick("/#contact")}
                className="bg-[#D4AF37] text-black hover:bg-[#C5A880] text-xs font-semibold px-6 py-3 tracking-widest uppercase transition-all duration-300"
              >
                Solicită Ofertă
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            {user && (
              <Link
                to="/admin"
                data-testid="navbar-mobile-admin-link"
                className="text-[#D4AF37]"
              >
                <Shield size={18} />
              </Link>
            )}
            <button
              data-testid="navbar-mobile-menu-toggle"
              onClick={() => setIsOpen(!isOpen)}
              className="text-white hover:text-[#D4AF37] transition-colors"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div 
          data-testid="navbar-mobile-menu"
          className="md:hidden bg-black/95 border-b border-white/10 px-4 pt-4 pb-6 space-y-4"
        >
          <div className="flex flex-col space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.name}
                data-testid={`navbar-mobile-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => handleNavClick(link.href)}
                className="text-left text-base font-medium text-white/90 hover:text-[#D4AF37] py-2 border-b border-white/5 tracking-wider uppercase"
              >
                {link.name}
              </button>
            ))}
          </div>

          <div className="pt-2">
            {user ? (
              <div className="flex items-center justify-between">
                <Link
                  to="/admin"
                  data-testid="navbar-mobile-admin-dashboard-link"
                  onClick={() => setIsOpen(false)}
                  className="bg-white/10 text-white text-center py-3 px-4 w-1/2 mr-2 text-xs uppercase tracking-widest font-semibold"
                >
                  Panou Admin
                </Link>
                <button
                  data-testid="navbar-mobile-logout-button"
                  onClick={() => {
                    logout();
                    setIsOpen(false);
                  }}
                  className="bg-red-950/40 text-red-400 text-center py-3 px-4 w-1/2 text-xs uppercase tracking-widest font-semibold border border-red-900/30"
                >
                  Deconectare
                </button>
              </div>
            ) : (
              <button
                data-testid="navbar-mobile-cta-button"
                onClick={() => handleNavClick("/#contact")}
                className="bg-[#D4AF37] text-black w-full text-center py-4 font-semibold text-xs tracking-widest uppercase transition-colors"
              >
                Solicită Ofertă
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
